import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpDown, Filter } from 'lucide-react';
import FilterableDataTable from '../../../components/ui/FilterableDataTable';
import SearchBar from '../../../components/ui/SearchBar';
import AnchorDropdownPanel from '../../../components/ui/AnchorDropdownPanel';
import DropdownSearch from '../../../components/ui/DropdownSearch';
import { FeatureGate } from '../../../contexts/FeatureFlagContext';
import toast from '../../../services/toast';
import { dropdown, table, cn } from '../../../components/ui/uiTheme';
import { SectionSubtitle } from '../../../components/ui/PageLayout';

function startOfLocalDayToIso(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

function endOfLocalDayToIso(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

function isoToDateInput(iso) {
  if (!iso || typeof iso !== 'string') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

/** Default: newest commit first. */
const DEFAULT_COMMITS_SORT = [{ id: 'commit', desc: true }];

/** Each sort field has descending and ascending (TanStack: `desc: true` = larger / newer timestamps first). */
const COMMITS_SORT_OPTIONS = [
  { key: 'commit-desc', columnId: 'commit', desc: true, label: 'Date (newest first)' },
  { key: 'commit-asc', columnId: 'commit', desc: false, label: 'Date (oldest first)' },
  { key: 'files-desc', columnId: 'commitFiles', desc: true, label: 'Files changed (high → low)' },
  { key: 'files-asc', columnId: 'commitFiles', desc: false, label: 'Files changed (low → high)' },
  { key: 'churn-desc', columnId: 'addDelChurn', desc: true, label: 'Churn ratio (high → low)' },
  { key: 'churn-asc', columnId: 'addDelChurn', desc: false, label: 'Churn ratio (low → high)' },
  { key: 'add-desc', columnId: 'commitAdditions', desc: true, label: 'Additions (high → low)' },
  { key: 'add-asc', columnId: 'commitAdditions', desc: false, label: 'Additions (low → high)' },
  { key: 'del-desc', columnId: 'commitDeletions', desc: true, label: 'Deletions (high → low)' },
  { key: 'del-asc', columnId: 'commitDeletions', desc: false, label: 'Deletions (low → high)' },
];

export default function RepositoryCommitsTab({
  repositoryId,
  tabCommits,
  tabCommitsMeta,
  tabCommitsLoading,
  commitsColumns,
  commitsListFilters,
  onCommitsListFiltersChange,
  commitFilterBranches,
  contributors,
  contributorsLoading,
}: any) {
  const [commitsSearch, setCommitsSearch] = useState('');
  const [commitsSort, setCommitsSort] = useState(DEFAULT_COMMITS_SORT);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const filterAnchorRef = useRef<any>(null);
  const sortAnchorRef = useRef<any>(null);
  const [draftBranch, setDraftBranch] = useState('');
  const [draftAuthor, setDraftAuthor] = useState('');
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');

  useEffect(() => {
    setCommitsSearch('');
    setCommitsSort(DEFAULT_COMMITS_SORT);
  }, [repositoryId]);

  useEffect(() => {
    if (!filterOpen) return;
    setDraftBranch(commitsListFilters.branch || '');
    setDraftAuthor(commitsListFilters.author || '');
    setDraftFrom(isoToDateInput(commitsListFilters.from));
    setDraftTo(isoToDateInput(commitsListFilters.to));
  }, [filterOpen, commitsListFilters]);

  const branchOptions = useMemo(
    () => [
      { name: '', label: 'Any branch' },
      ...commitFilterBranches.map((b) => ({ name: b.name, label: b.name })),
    ],
    [commitFilterBranches]
  );

  const contributorOptions = useMemo(() => {
    const rows = Array.isArray(contributors) ? contributors : [];
    const opts = [{ filterText: '', label: 'Any contributor' }];
    for (const c of rows) {
      const filterText =
        (c.github && String(c.github).trim()) ||
        (c.name && String(c.name).trim()) ||
        (c.email && String(c.email).trim()) ||
        '';
      if (!filterText) continue;
      const label = c.name || c.github || c.email || 'Contributor';
      const sub = c.github ? `@${c.github}` : c.email || '';
      opts.push({ filterText, label, sublabel: sub } as any);
    }
    return opts;
  }, [contributors]);

  const hasActiveCommitFilters = Boolean(
    commitsListFilters.branch?.trim() ||
    commitsListFilters.author?.trim() ||
    commitsListFilters.from?.trim() ||
    commitsListFilters.to?.trim()
  );

  const handleApplyFilters = () => {
    if (draftFrom && draftTo && draftFrom > draftTo) {
      toast.error('Start date must be on or before the end date.');
      return;
    }
    onCommitsListFiltersChange({
      branch: draftBranch?.trim() || '',
      author: draftAuthor?.trim() || '',
      from: draftFrom ? startOfLocalDayToIso(draftFrom) : '',
      to: draftTo ? endOfLocalDayToIso(draftTo) : '',
    });
    setFilterOpen(false);
  };

  const handleClearAppliedFilters = () => {
    onCommitsListFiltersChange({ branch: '', author: '', from: '', to: '' });
    setDraftBranch('');
    setDraftAuthor('');
    setDraftFrom('');
    setDraftTo('');
    setFilterOpen(false);
  };

  return (
    <div role="tabpanel" id="repo-tabpanel-commits" aria-labelledby="repo-tab-commits">
      <FeatureGate flag="ui.repositoryDetail.commitsTab">
        <div className={cn(table.wrapper, 'overflow-hidden')}>
          <div className="px-4 py-3 border-b border-dark-border flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="min-w-0 flex-1">
              <SectionSubtitle>Commits</SectionSubtitle>
              <p className="text-sm text-slate-400 mt-0.5">
                {tabCommitsMeta?.total != null && <>Total: {tabCommitsMeta.total} · </>}
                Showing up to {tabCommits.length} loaded. Search and sort apply to loaded rows;
                filters refetch from the server.
              </p>
            </div>
            {!tabCommitsLoading && (
              <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto sm:max-w-xl md:max-w-2xl">
                <div className="min-w-0 flex-1 sm:min-w-[12rem] sm:max-w-md">
                  <SearchBar
                    value={commitsSearch}
                    onChange={setCommitsSearch}
                    placeholder="Search commits…"
                  />
                </div>
                <button
                  ref={sortAnchorRef}
                  type="button"
                  className="btn-icon-mdl h-10 w-10 shrink-0 rounded-xl border border-dark-border"
                  onClick={() => setSortOpen((v) => !v)}
                  aria-label="Sort commits"
                  title="Sort commits"
                  aria-expanded={sortOpen}
                  aria-haspopup="dialog"
                  aria-controls="repo-commits-sort-menu"
                >
                  <ArrowUpDown className="h-5 w-5 text-slate-200" aria-hidden />
                </button>
                <button
                  ref={filterAnchorRef}
                  type="button"
                  className="btn-icon-mdl h-10 w-10 shrink-0 rounded-xl border border-dark-border"
                  onClick={() => setFilterOpen((v) => !v)}
                  aria-label="Filter commits by branch, contributor, or date"
                  title="Filter commits"
                  aria-expanded={filterOpen}
                  aria-haspopup="dialog"
                  aria-controls="repo-commits-filter-menu"
                >
                  <span className="relative inline-flex">
                    <Filter className="h-5 w-5 text-slate-200" aria-hidden />
                    {hasActiveCommitFilters && (
                      <span
                        className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-vivid-red ring-2 ring-dark-surface"
                        aria-hidden
                      />
                    )}
                  </span>
                </button>
              </div>
            )}
          </div>
          <FilterableDataTable
            key={`commits-${repositoryId}`}
            data={tabCommits}
            columns={commitsColumns}
            getRowId={(row) => String(row.id)}
            isLoading={tabCommitsLoading}
            emptyMessage="No commits found."
            searchPlaceholder="Search commits…"
            initialSorting={DEFAULT_COMMITS_SORT}
            sorting={commitsSort}
            onSortingChange={setCommitsSort}
            showColumnHeaders={false}
            globalFilter={commitsSearch}
            onGlobalFilterChange={setCommitsSearch}
            hideTopSearchRow
          />

          <AnchorDropdownPanel
            open={sortOpen}
            onClose={() => setSortOpen(false)}
            anchorRef={sortAnchorRef}
            id="repo-commits-sort-menu"
            ariaLabel="Sort commits"
            panelWidth={300}
          >
            <div className="space-y-1" role="list">
              {COMMITS_SORT_OPTIONS.map((opt) => {
                const active =
                  commitsSort.length === 1 &&
                  commitsSort[0].id === opt.columnId &&
                  commitsSort[0].desc === opt.desc;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    role="listitem"
                    className={cn(
                      'w-full rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors',
                      active ? 'bg-brand-purple/15 text-brand-purple-light' : dropdown.item
                    )}
                    onClick={() => {
                      setCommitsSort([{ id: opt.columnId, desc: opt.desc }]);
                      setSortOpen(false);
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </AnchorDropdownPanel>

          <AnchorDropdownPanel
            open={filterOpen}
            onClose={() => setFilterOpen(false)}
            anchorRef={filterAnchorRef}
            id="repo-commits-filter-menu"
            title="Filter commits"
            titleIcon={<Filter className="h-5 w-5 shrink-0 text-vivid-red" aria-hidden />}
            panelWidth={400}
          >
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="commits-filter-branch"
                  className="block text-sm font-medium text-slate-300 mb-1.5"
                >
                  Branch
                </label>
                <DropdownSearch
                  id="commits-filter-branch"
                  options={branchOptions}
                  value={draftBranch}
                  onChange={(opt) => setDraftBranch(opt?.name ?? '')}
                  getOptionValue={(o) => o.name}
                  getOptionLabel={(o) => o.label}
                  placeholder="Search branches…"
                  emptyMessage={
                    commitFilterBranches.length
                      ? 'No matching branch'
                      : 'No branches loaded — sync the repository'
                  }
                  disabled={!commitFilterBranches.length}
                  compact
                />
              </div>
              <div>
                <label
                  htmlFor="commits-filter-contributor"
                  className="block text-sm font-medium text-slate-300 mb-1.5"
                >
                  Contributor
                </label>
                <DropdownSearch
                  id="commits-filter-contributor"
                  options={contributorOptions}
                  value={draftAuthor}
                  onChange={(opt) => setDraftAuthor(opt?.filterText ?? '')}
                  getOptionValue={(o) => o.filterText}
                  getOptionLabel={(o) => o.label}
                  getOptionSublabel={(o) => o.sublabel}
                  placeholder="Search contributors…"
                  emptyMessage="No matching contributor"
                  disabled={contributorsLoading}
                  compact
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="commits-filter-from"
                    className="block text-sm font-medium text-slate-300 mb-1.5"
                  >
                    Start date
                  </label>
                  <input
                    id="commits-filter-from"
                    type="date"
                    value={draftFrom}
                    onChange={(e) => setDraftFrom(e.target.value)}
                    className="input-mdl-sm w-full"
                  />
                </div>
                <div>
                  <label
                    htmlFor="commits-filter-to"
                    className="block text-sm font-medium text-slate-300 mb-1.5"
                  >
                    End date
                  </label>
                  <input
                    id="commits-filter-to"
                    type="date"
                    value={draftTo}
                    onChange={(e) => setDraftTo(e.target.value)}
                    className="input-mdl-sm w-full"
                  />
                </div>
              </div>
              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:gap-3">
                <button
                  type="button"
                  className="btn-secondary-mdl-sm"
                  onClick={() => setFilterOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-secondary-mdl-sm"
                  onClick={handleClearAppliedFilters}
                  disabled={
                    !hasActiveCommitFilters && !(draftBranch || draftAuthor || draftFrom || draftTo)
                  }
                >
                  Clear filters
                </button>
                <button type="button" className="btn-primary-mdl-sm" onClick={handleApplyFilters}>
                  Apply
                </button>
              </div>
            </div>
          </AnchorDropdownPanel>
        </div>
      </FeatureGate>
    </div>
  );
}
